import type { StudentAssessmentEvent } from '@court-sync/shared';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { PaginatedList } from '@/components/ui/PaginatedList';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { Calendar, CheckCircle2, BookOpen, Activity } from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatAssessmentSkillLabel } from '@/lib/sportPresentation';

export interface AssessmentHistoryTimelineProps {
  events: StudentAssessmentEvent[];
  programNameById?: Record<string, string>;
  className?: string;
  /** When true, `pagination` / `onPageChange` / `loading` drive server-backed pages (newest first on page 1). */
  paginated?: boolean;
  pagination?: PaginationState | null;
  onPageChange?: (page: number) => void;
  loading?: boolean;
}

function formatWhen(d: Date) {
  const x = new Date(d);
  return x.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function AssessmentEventCard({
  ev,
  programNameById = {},
}: {
  ev: StudentAssessmentEvent;
  programNameById?: Record<string, string>;
}) {
  return (
    <Card className="border-border/80">
      <CardContent className="py-4">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {ev.source === 'program_skill_note' ? (
              <BookOpen className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            ) : (
              <Activity className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            )}
            <span className="font-medium text-foreground">{ev.coachName}</span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" aria-hidden />
              {formatWhen(ev.createdAt)}
            </span>
          </div>
          <Badge variant="outline" className="text-xs font-normal">
            {ev.source === 'program_skill_note' ? 'Pathway skill' : 'Activity assessment'}
          </Badge>
        </div>

        {ev.source === 'program_skill_note' && ev.programSkillName && (
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm text-foreground">
              <span className="text-muted-foreground">Skill:</span>{' '}
              {programNameById[ev.programId ?? ''] ?? 'Program'} · {ev.programSkillName}
              {ev.rating != null ? ` · ${ev.rating}/5` : ''}
            </span>
            {ev.skillAchieved && (
              <Badge className="bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200 gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                Achieved
              </Badge>
            )}
          </div>
        )}

        {(ev.source === 'tennis_progress_update' || ev.source === 'sport_progress_update') && (
          <div className="mb-2 text-sm">
            <span className="text-muted-foreground">Level:</span>{' '}
            <span className="font-medium capitalize">{ev.skillLevel ?? '—'}</span>
            {ev.tennisSkillRatings && Object.keys(ev.tennisSkillRatings).length > 0 && (
              <ul className="mt-2 space-y-0.5 text-muted-foreground">
                {Object.entries(ev.tennisSkillRatings).map(([k, rating]) => (
                  <li key={k}>
                    {formatAssessmentSkillLabel(k)}: {rating}/5
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {ev.narrative.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap border-t border-border/60 pt-2 mt-2">
            {ev.narrative}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function AssessmentHistoryTimeline({
  events,
  programNameById = {},
  className,
  paginated = false,
  pagination,
  onPageChange,
  loading = false,
}: AssessmentHistoryTimelineProps) {
  const emptyCard = (
    <Card className={className}>
      <CardContent className="py-10 text-center text-gray-500 dark:text-gray-400 text-sm">
        No assessment history yet. Pathway progress notes and tennis skill updates will appear here.
      </CardContent>
    </Card>
  );

  if (paginated) {
    if (!pagination) {
      return (
        <div className={cn('flex justify-center py-12', className)}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400" />
        </div>
      );
    }
    if (pagination.total === 0) {
      return emptyCard;
    }
    return (
      <div className={className}>
        <PaginatedList<StudentAssessmentEvent>
          data={events}
          pagination={pagination}
          loading={loading}
          onPageChange={(page) => onPageChange?.(page)}
          getItemKey={(ev) => ev.id}
          renderItem={(ev) => <AssessmentEventCard ev={ev} programNameById={programNameById} />}
        />
      </div>
    );
  }

  if (events.length === 0) {
    return emptyCard;
  }

  return (
    <div className={cn('space-y-3', className)} role="list" aria-label="Assessment history">
      {events.map((ev) => (
        <AssessmentEventCard key={ev.id} ev={ev} programNameById={programNameById} />
      ))}
    </div>
  );
}
